import type { Ref } from 'vue';
import { readonly, ref } from 'vue';

import { useAtomic } from '@/use/useAtomic';
import { PleasedClient } from '@/pleased-sdk/PleasedClient';
import { useBotUser } from '@/stores/useBotUser';
import { useI18n } from '@/plugins/i18n';
import { Bridge, platform } from '@/common/bridge';
import { store } from '@/store';
import { analytics, VOICE_EVENTS } from '@/common/logs';
import { devLog } from '@/common/debug';

import type { PleasedVoiceRequest, PlsDialogState } from '../cf-v1/usePleasedSupport.types';

import { useConfig } from '@/stores/useConfig';
import { storeToRefs } from 'pinia';

const getIsMobileSdk = () => false;
const hasPermission = async () => false;

export function usePleasedVoiceChat() {
  const configStore = useConfig();
  const { config } = storeToRefs(configStore);
  const appName = config.value?.appName;

  const voiceChatId = ref<number | null>();
  // const voice = computed(() => (voiceChatId.value ? PleasedClient.get('voice', voiceChatId.value) : null));

  const voiceChatDialog = ref<PlsDialogState>({
    open: false,
    content: '',
    title: '',
    actions: [],
  });
  const i18n = useI18n();

  const createPermissionDialog = () =>
    ({
      open: true,
      title: i18n.t('page.voice.permission.title', { appName: appName || 'Pleased' }),
      content: i18n.t('page.voice.permission.reason', { appName: appName || 'Pleased' }),
      actions: [
        {
          id: 1,
          text: i18n.t('page.voice.permission.not_now'),
          action: () => {
            analytics.trackVoiceEvent({ name: VOICE_EVENTS.VOICE_MICROPHONE_PERMISSION_DENIED });
            voiceChatDialog.value.open = false;
          },
        },
        {
          id: 2,
          text: 'Ok',
          action: () => {
            analytics.trackVoiceEvent({ name: VOICE_EVENTS.VOICE_MICROPHONE_PERMISSION_OPEN_SETTINGS });
            voiceChatDialog.value.open = false;
            Bridge.listenFromParent('APP_GO_SETTING', (value: unknown) => {
              analytics.trackVoiceEvent({
                name: VOICE_EVENTS.VOICE_MICROPHONE_PERMISSION_OPEN_SETTINGS_RESPONSE,
                metadata: { value },
              });
            });

            Bridge.sendToParent('APP_GO_SETTING');
          },
        },
      ],
    }) as PlsDialogState;

  const createDisclaimerDialog = (accepted: (val: unknown) => void, canceled: (err: string) => void) =>
    ({
      open: true,
      title: '',
      content: i18n.t('page.voice.disclaimer'),
      actions: [
        {
          id: 1,
          text: i18n.t('page.voice.cancel'),
          action: () => {
            voiceChatDialog.value.open = false;
            canceled('[VOICE] Disclaimer rejected.');
          },
        },
        {
          id: 2,
          text: i18n.t('page.connect.call'),
          color: 'primary',
          action: () => {
            voiceChatDialog.value.open = false;
            accepted(null);
          },
        },
      ],
    }) as PlsDialogState;

  const [createVoiceChat, isCreatingVoice, errorCreatingVoice] = useAtomic(
    async (request: PleasedVoiceRequest['payload']) => {
      const { user } = useBotUser();
      const supportTwilioVoiceCall = store.getters['user/supportTwilioVoiceCall'] as boolean;
      const useNativeHost = platform === 'app' && supportTwilioVoiceCall;

      devLog.debug('[PLEASED_VOICE_CHAT] Creating voice chat', { request });

      analytics.trackVoiceEvent({
        name: VOICE_EVENTS.VOICE_CREATE_REQUESTED,
        metadata: {
          useNativeHost,
          hasDescription: Boolean(request.description),
          hasTicketId: Boolean(request.ticketId),
          hasUserSelections: Array.isArray(request.userSelections) && request.userSelections.length > 0,
        },
      });

      if (getIsMobileSdk()) {
        // For desktop devices, the permission will be asked at later point.
        // When the voice chat is starting.
        const mobileSdkPermission = getIsMobileSdk() ? await hasPermission() : true;

        if (!mobileSdkPermission) {
          voiceChatDialog.value = createPermissionDialog();
          return;
        }
      }

      // await new Promise((resolve, reject) => {
      //   voiceChatDialog.value = createDisclaimerDialog(resolve, reject);
      // });

      const customerAppVersion = store.state.user.customerAppVersion || undefined;

      const voice = PleasedClient.voice({
        user,
        ...request,
        ...(customerAppVersion ? { customerAppVersion } : {}),
        ...(useNativeHost ? { skipWebSDKInitialization: true } : {}),
      });
      await voice.create();
      devLog.debug('[PLEASED_VOICE_CHAT] Voice chat created successfully', { voice });
      voiceChatId.value = voice.id as number;
      return voice;
    }
  );

  function resetError() {
    errorCreatingVoice.value = '';
  }

  function reset() {
    resetError();
    voiceChatId.value = null;
  }

  return {
    voiceChatId: readonly(voiceChatId),
    // voice,
    createVoiceChat,
    voiceChatDialog: readonly(voiceChatDialog) as Ref<PlsDialogState>,
    resetError,
    isCreatingVoice,
    errorCreatingVoice,
    reset,
  };
}
